import React from 'react';
import BottomNavigationAction from '@material-ui/core/BottomNavigationAction';
import {Badge} from '@material-ui/core';
import classnames from 'classnames';
import * as PropTypes from 'prop-types';
import BottomNavigation from '@material-ui/core/BottomNavigation';
import ExploreIcon from 'Components/common/Icons/ExploreIcon';
import ExploreSelectedIcon from 'Components/common/Icons/ExploreSelectedIcon';
import {EXPLORER_NAVIGATION, EXPLORER_NAVIGATION_LABELS} from 'Utils/explorer/explorerConstants';
import EventsIcon from 'Components/common/Icons/EventsIcon';
import EventsSelectedIcon from 'Components/common/Icons/EventsSelectedIcon';
import SearchIcon from 'Components/common/Icons/SearchIcon';
import SearchSelectedIcon from 'Components/common/Icons/SearchSelectedIcon';
import DirectoryIcon from 'Components/common/Icons/DirectoryIcon';
import DirectionsIcon from 'Components/common/Icons/DirectionsIcon';
import PromotionsIcon from 'Components/common/Icons/PromotionsIcon';
import PromotionsSelectedIcon from 'Components/common/Icons/PromotionsSelectedIcon';
import QuickLinks from 'Components/Explorer/Cards/SearchCard/QuickLinks';
import * as analytics from 'Utils/analytics';
import {Desktop, useDesktopMediaQuery} from 'Utils/ResponsiveUtil';
import {prependDigitalAssetsUrl} from 'Utils/prependDigitalAssetsUrl';
import {PAM_BANNER_LOGO} from 'Utils/constants';

const MenuChildren = ({menus, selectedMenuItemId, isDesktop}) =>
    menus.map(({key, displayLabel, icon, iconSelected, count, color}) => {
        const MenuIcon = selectedMenuItemId === key && iconSelected ? iconSelected : icon;

        const menuItemId = isDesktop
            ? EXPLORER_NAVIGATION[key].desktopMenuItemId ?? EXPLORER_NAVIGATION[key].menuItemId
            : EXPLORER_NAVIGATION[key].menuItemId;

        return (
            <BottomNavigationAction
                className={classnames('explorer-menu-item', {
                    'explorer-menu-selected': menuItemId === selectedMenuItemId,
                })}
                style={{
                    backgroundColor: menuItemId === selectedMenuItemId && color,
                }}
                key={key}
                value={key}
                aria-label={key}
                label={displayLabel}
                icon={
                    <>
                        {key === EXPLORER_NAVIGATION_LABELS.experiences ? (
                            <Badge
                                badgeContent={count}
                                className="menu-counter-badge"
                                overlap="rectangular"
                            >
                                <MenuIcon title={key} />
                            </Badge>
                        ) : (
                            <MenuIcon title={key} />
                        )}
                    </>
                }
            />
        );
    });

const Menu = ({
    selectedMenuItemKey,
    navigate,
    hideCard,
    menuTabConfig,
    numberOfEvents,
    setQuickLinkFn,
    searchFilters,
    quickLinks,
    projectName,
    setJourneyStage,
    setJourneyStop,
    brandingImageUrl,
    resetMap,
}) => {
    const isDesktop = useDesktopMediaQuery();

    const reversedSearchFilters = [].concat(searchFilters).reverse();
    const reversedQuickLinks = [].concat(quickLinks).reverse();

    let selectedMenuItemId = isDesktop
        ? EXPLORER_NAVIGATION[selectedMenuItemKey].desktopMenuItemId ??
          EXPLORER_NAVIGATION[selectedMenuItemKey].menuItemId
        : EXPLORER_NAVIGATION[selectedMenuItemKey].menuItemId;

    const MenuItems = {
        search: {
            displayLabel: menuTabConfig.menuLabelSearch,
            key: EXPLORER_NAVIGATION_LABELS.search,
            icon: SearchIcon,
            iconSelected: SearchSelectedIcon,
            color: menuTabConfig.menuColourSearch,
        },
        explore: {
            displayLabel: menuTabConfig.menuLabelExplore,
            key: EXPLORER_NAVIGATION_LABELS.explore,
            icon: ExploreIcon,
            iconSelected: ExploreSelectedIcon,
            color: menuTabConfig.menuColourExplore,
        },
        directory: {
            displayLabel: menuTabConfig.menuLabelDirectory,
            key: EXPLORER_NAVIGATION_LABELS.tenants,
            icon: DirectoryIcon,
            color: menuTabConfig.menuColourDirectory,
        },
        directions: {
            displayLabel: menuTabConfig.menuLabelDirections,
            key: EXPLORER_NAVIGATION_LABELS.directions,
            icon: DirectionsIcon,
            color: menuTabConfig.menuColourDirections,
        },
        promotions: {
            displayLabel: menuTabConfig.menuLabelPromotions,
            key: EXPLORER_NAVIGATION_LABELS.promotions,
            icon: PromotionsIcon,
            iconSelected: PromotionsSelectedIcon,
            color: menuTabConfig.menuColourPromotions,
        },
        events: {
            displayLabel: menuTabConfig.menuLabelExperiences,
            key: EXPLORER_NAVIGATION_LABELS.events,
            icon: EventsIcon,
            iconSelected: EventsSelectedIcon,
            count: numberOfEvents,
            color: menuTabConfig.menuColourExperiences,
        },
    };

    const menus = isDesktop
        ? [
              MenuItems.explore,
              MenuItems.search,
              ...(menuTabConfig.enableDirectionsOnExplorer ? [MenuItems.directions] : []),
              ...(menuTabConfig.enableDirectoryWithKiosk ? [MenuItems.directory] : []),
              ...(menuTabConfig.enableDirectoryWithKiosk && menuTabConfig.enablePromotionsWithKiosk
                  ? [MenuItems.promotions]
                  : []),
              ...(menuTabConfig.enableExperiences ? [MenuItems.events] : []),
          ]
        : [
              MenuItems.search,
              ...(!(
                  menuTabConfig.enableDirectoryWithKiosk && menuTabConfig.enablePromotionsWithKiosk
              )
                  ? [MenuItems.explore]
                  : []),
              ...(menuTabConfig.enableDirectoryWithKiosk ? [MenuItems.directory] : []),
              ...(menuTabConfig.enableDirectoryWithKiosk && menuTabConfig.enablePromotionsWithKiosk
                  ? [MenuItems.explore]
                  : []),
              ...(menuTabConfig.enableDirectoryWithKiosk && menuTabConfig.enablePromotionsWithKiosk
                  ? [MenuItems.promotions]
                  : []),
              ...(menuTabConfig.enableExperiences ? [MenuItems.events] : []),
          ];

    // in case when selected menu item is not available we highlight "Explore" menu item
    selectedMenuItemId = menus.find(menuItem => menuItem.key === selectedMenuItemId)
        ? selectedMenuItemId
        : EXPLORER_NAVIGATION_LABELS.explore;

    return (
        <div className="explorer-menu-wrapper">
            <Desktop>
                <img
                    alt={projectName}
                    src={
                        brandingImageUrl
                            ? prependDigitalAssetsUrl(brandingImageUrl)
                            : PAM_BANNER_LOGO
                    }
                    className="desktop-branding-image"
                />
            </Desktop>
            <BottomNavigation
                className="explorer-menu"
                value={selectedMenuItemId}
                onChange={(event, newSelection) => {
                    if (
                        newSelection === EXPLORER_NAVIGATION_LABELS.explore &&
                        selectedMenuItemId === newSelection
                    ) {
                        if (resetMap) {
                            resetMap();
                        }
                        return;
                    }
                    if (
                        newSelection === EXPLORER_NAVIGATION_LABELS.directions &&
                        selectedMenuItemId === newSelection
                    ) {
                        return;
                    }
                    hideCard();
                    setJourneyStop({reset: true});
                    setJourneyStage(null);
                    navigate(newSelection);
                    analytics.sendMenuAnalyticsEvent(newSelection);
                }}
                showLabels
            >
                {MenuChildren({menus, selectedMenuItemId, isDesktop})}
            </BottomNavigation>
            <Desktop>
                <QuickLinks
                    quickLinks={reversedQuickLinks}
                    searchFilters={reversedSearchFilters}
                    selectQuickLink={setQuickLinkFn}
                />
            </Desktop>
        </div>
    );
};
Menu.propTypes = {
    selectedMenuItemKey: PropTypes.string.isRequired,
    navigate: PropTypes.func.isRequired,
    hideCard: PropTypes.func.isRequired,
    menuTabConfig: PropTypes.object,
    numberOfEvents: PropTypes.number,
    searchFilters: PropTypes.array,
    quickLinks: PropTypes.array,
    setQuickLinkFn: PropTypes.func,
    brandingImageUrl: PropTypes.string,
    projectName: PropTypes.string,
    setJourneyStage: PropTypes.func,
    setJourneyStop: PropTypes.func,
    resetMap: PropTypes.func,
};

export default Menu;
